// Chart.js time-axis ticks in UTC. The charts import chartjs-adapter-date-fns, which has no timezone support:
// the `adapters.date.zone` option belongs to the luxon adapter and is ignored, so ticks followed the browser's
// zone under a title that said UTC (#849). The tooltips already format in UTC; this does the same for the axis.

const DAY_MS = 24 * 60 * 60 * 1000;

const monthYear = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", year: "numeric" });
const monthDay = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
const monthDayTime = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

interface TimeTick {
  value: number;
}

/**
 * `scales.x.ticks.callback` for a time axis: the tick's instant formatted in UTC at the granularity the axis is
 * using. The date-fns adapter's own labels (`displayFormats`) are replaced, so the format is read off the ticks
 * themselves: month names when every tick is the first of a UTC month, dates when every tick is a UTC midnight,
 * otherwise date and time.
 */
export const utcTickLabel = (_label: unknown, index: number, ticks: TimeTick[]): string => {
  const tick = ticks?.[index];
  if (!tick || !Number.isFinite(tick.value)) return "";
  const date = new Date(tick.value);
  if (ticks.length < 2) return monthDayTime.format(date);
  const allMidnight = ticks.every((t) => Number.isFinite(t.value) && t.value % DAY_MS === 0);
  if (!allMidnight) return monthDayTime.format(date);
  const allFirstOfMonth = ticks.every((t) => new Date(t.value).getUTCDate() === 1);
  return allFirstOfMonth ? monthYear.format(date) : monthDay.format(date);
};

// ============================================ Sparkline ============================================

export interface SparklinePoint {
  t: number;
  value: number;
  low?: number;
  high?: number;
}

interface SparklineLayer {
  past: string | null;
  future: string | null;
  band: string | null;
}

interface SparklinePaths {
  layers: SparklineLayer[];
  nowX: number | null;
}

// { timestamp, value } records (the shape every preview fetcher returns) as time-sorted points, dropping unparseable rows
export const recordsToPoints = (records: Array<{ timestamp: string | Date; value: number }>): SparklinePoint[] =>
  records
    .map((r) => ({ t: new Date(r.timestamp).getTime(), value: r.value }))
    .filter((p) => typeof p.value === "number" && !Number.isNaN(p.value) && !Number.isNaN(p.t))
    .sort((a, b) => a.t - b.t);

// Bucket-average down to at most maxPoints, so a long hourly series stays a smooth line at card size
const downsamplePoints = (points: SparklinePoint[], maxPoints: number): SparklinePoint[] => {
  if (points.length <= maxPoints) return points;
  const size = Math.ceil(points.length / maxPoints);
  const out: SparklinePoint[] = [];
  for (let i = 0; i < points.length; i += size) {
    const bucket = points.slice(i, i + size);
    const avg = (pick: (p: SparklinePoint) => number | undefined) => {
      const vals = bucket.map(pick).filter((v): v is number => typeof v === "number");
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : undefined;
    };
    out.push({ t: avg((p) => p.t)!, value: avg((p) => p.value)!, low: avg((p) => p.low), high: avg((p) => p.high) });
  }
  return out;
};

/** SVG paths for a card sparkline in a width×height box: each series split at now into history and forecast, on one shared time axis with its own value scale; only the first series draws its quantile band. horizonDays trims each series to that window either side of now when it holds data. */
export const buildSparklinePaths = (
  rawSeries: SparklinePoint[][],
  width: number,
  height: number,
  { now = Date.now(), horizonDays }: { now?: number; horizonDays?: number } = {}
): SparklinePaths | null => {
  const span = (horizonDays || 0) * DAY_MS;
  const series = rawSeries
    .map((raw) => {
      const windowed = span ? raw.filter((p) => p.t >= now - span && p.t <= now + span) : raw;
      return downsamplePoints(windowed.length > 1 ? windowed : raw, 48);
    })
    .map((points, i): SparklinePoint[] => (i === 0 ? points : points.map(({ t, value }) => ({ t, value }))));
  const lead = series[0];
  if (!lead || lead.length < 2) return null;

  // The lead sets the time window; a forecast lead reaches back as far as it looks ahead, so other lines show the history it continues
  const leadMin = lead[0].t;
  const tMax = lead[lead.length - 1].t;
  const tMin = series.length > 1 && now < tMax ? Math.min(leadMin, now - (tMax - now)) : leadMin;
  const pad = 4;
  const xIn = (from: number, to: number) => (t: number) => (to === from ? width / 2 : ((t - from) / (to - from)) * width);

  // A line with data in the window shares its time axis; one outside it shows its own stretch nearest the window (latest if it ended before, earliest if it starts after) across the full width — shape only, not time-aligned
  const placed = series.map((points, i): { points: SparklinePoint[]; x: (t: number) => number } => {
    const inWindow = i === 0 ? points : points.filter((p) => p.t >= tMin && p.t <= tMax);
    if (inWindow.length > 1 || points.length < 2) return { points: inWindow, x: xIn(tMin, tMax) };
    const raw = rawSeries[i];
    const after = raw[0].t > tMax;
    const edge = after ? raw[0].t : raw[raw.length - 1].t;
    const near = raw.filter((p) => (after ? p.t <= edge + (tMax - tMin) : p.t >= edge - (tMax - tMin)));
    const stretch = downsamplePoints(near.length > 1 ? near : raw, 48).map(({ t, value }) => ({ t, value }));
    return { points: stretch, x: xIn(stretch[0].t, stretch[stretch.length - 1].t) };
  });

  // Each line fills the height on its own scale, so a 24 °C dataset shows its shape beside a 150 MW forecast; heights are not comparable across lines
  const layers = placed.map(({ points, x }): SparklineLayer => {
    if (points.length < 2) return { past: null, future: null, band: null };
    const vMin = Math.min(...points.map((p) => p.low ?? p.value));
    const vMax = Math.max(...points.map((p) => p.high ?? p.value));
    // A flat series sits mid-height rather than dividing by zero
    const y = (v: number) => (vMax === vMin ? height / 2 : pad + (1 - (v - vMin) / (vMax - vMin)) * (height - pad * 2));
    const line = (pts: SparklinePoint[]) =>
      pts.length > 1 ? pts.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ") : null;
    // The point either side of now belongs to both halves, so the two strokes meet
    const splitAt = points.findIndex((p) => p.t > now);
    const past = splitAt === -1 ? points : points.slice(0, Math.max(splitAt, 1));
    const future = splitAt === -1 ? [] : points.slice(Math.max(splitAt - 1, 0));
    const banded = points.filter((p) => p.low !== undefined && p.high !== undefined);
    const band =
      banded.length > 1
        ? [
            ...banded.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.high!).toFixed(1)}`),
            ...banded.slice().reverse().map((p) => `L${x(p.t).toFixed(1)},${y(p.low!).toFixed(1)}`),
            "Z",
          ].join(" ")
        : null;
    return { past: line(past), future: line(future), band };
  });

  return { layers, nowX: now > tMin && now < tMax ? xIn(tMin, tMax)(now) : null };
};
